import { useQuery } from "@apollo/client/react";
import { CurrentUserDocument } from "@/graphql/auth.generated";

/**
 * Is the signed-in person a platform administrator, and which organization should a page scope to.
 *
 * Both answers together because getting the first right and the second wrong is what broke the
 * Data, Library and Videos pages: each one derived its organization from
 * `memberships[0]?.organization.id ?? ""` and skipped its query when that was empty. A platform
 * admin belongs to **no** organization, so those pages asked for nothing and reported "You are not
 * a member of an organization" to the one role allowed to see everything.
 *
 * `organizationId` is therefore `undefined` for an admin, which the API reads as "every
 * organization", and their own org for anyone else. A page passes it straight through instead of
 * deciding for itself.
 *
 * `ownOrganizationId` is the membership alone, with no admin override — role and membership are
 * edited independently on the Users page, so an admin can still carry a real one. Use it for
 * anything that needs a concrete owner (an upload, a new record) rather than a list to scope,
 * since `organizationId` deliberately hides that membership for an admin.
 *
 * `role` rather than a permissions array because that is what `currentUser` exposes to the client;
 * the server re-derives this from its own context and is the thing that actually enforces it. This
 * hook decides what to *ask for*, never what is allowed.
 */
export const usePlatformAdmin = (): {
    isPlatformAdmin: boolean;
    organizationId: string | undefined;
    ownOrganizationId: string | undefined;
    loading: boolean;
} => {
    const { data, loading } = useQuery(CurrentUserDocument);
    const role = data?.currentUser?.role;
    const isPlatformAdmin = role === "ADMIN" || role === "SYSTEM";
    const ownOrganizationId = data?.currentUser?.memberships[0]?.organization.id;
    return {
        isPlatformAdmin,
        organizationId: isPlatformAdmin ? undefined : ownOrganizationId,
        ownOrganizationId,
        loading,
    };
};
